import React, { useState, useEffect, useMemo } from 'react';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Spinner from 'react-bootstrap/Spinner';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import { BOOKMAKER_ALIASES } from '../utils/constants';
import { useBooks } from '../contexts/BooksContext';
import AddToScratchpadLink from './AddToScratchpadLink';
import ModalBrandingBar from './ModalBrandingBar';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const SoccerPlayerModal = ({
  show,
  onHide,
  playerName,
  playerTeam,
  playerPosition,
  playerId,
  gameId,
  game,
  market,
  point,
  getTeamLogoUrl,
  getBookLogoUrl,
  onOddsClick,
  onRemoveBet,
  betslipItems = [],
  bookmakers = [],
  onToggleScratchpad,
  isInScratchpad,
}) => {
  const { isBookEnabledForGraphs } = useBooks();
  const [playerData, setPlayerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMarketType, setSelectedMarketType] = useState('goals');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [tooltipData, setTooltipData] = useState(null);

  // Chart state
  const [showChart, setShowChart] = useState(false);
  const [chartMarket, setChartMarket] = useState(null);
  const [chartPoint, setChartPoint] = useState(null);
  const [chartIsOver, setChartIsOver] = useState(true);
  const [oddsHistoryData, setOddsHistoryData] = useState(null);
  const [loadingChart, setLoadingChart] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate implied probability from American odds
  const calculateImpliedProbability = (odds) => {
    if (!odds || odds === '-') return null;
    const numericOdds = parseInt(odds);
    if (isNaN(numericOdds)) return null;

    if (numericOdds > 0) {
      return (100 / (numericOdds + 100)) * 100;
    } else {
      return (Math.abs(numericOdds) / (Math.abs(numericOdds) + 100)) * 100;
    }
  };

  // Helper to get the actual data key for a bookmaker
  const getBookmakerDataKey = (displayBook) => {
    return displayBook;
  };

  // Handle odds cell click — on desktop this positions a popover tooltip,
  // on mobile it opens a bottom sheet. betMeta carries the full bet shape so
  // the mobile sheet's Add/Remove button can call onOddsClick / onRemoveBet
  // without the caller repeating the betslip wiring on every tap.
  const handleOddsClick = (event, odds, book, point, bestBook, bestBookDiff, lineBooks, betMeta = null) => {
    if (!odds || odds === '-') return;

    const impliedProb = calculateImpliedProbability(odds);
    if (!impliedProb) return;

    // Calculate edge using the SAME logic as getTierStyle for consistency
    let edge = 0;
    if (lineBooks) {
      const allOdds = Object.values(lineBooks)
        .map(b => b.over || b.yes)
        .filter(o => o !== null && o !== undefined);

      if (allOdds.length >= 2) {
        const bestOdds = Math.max(...allOdds);
        const secondBestOdds = Math.max(...allOdds.filter(o => o !== bestOdds));

        if (odds === bestOdds) {
          if (bestOdds > 0 && secondBestOdds < 0) {
            const spreadPositive = bestOdds - 100;
            const spreadNegative = secondBestOdds - (-100);
            const totalSpread = spreadPositive - spreadNegative;
            edge = Math.abs(totalSpread / secondBestOdds) * 100;
          } else {
            edge = ((bestOdds - secondBestOdds) / Math.abs(secondBestOdds)) * 100;
          }
        }
      }
    }

    let x, y;
    if (isMobile) {
      x = window.innerWidth / 2 - 100;
      y = Math.min(event.clientY, window.innerHeight - 150);
    } else {
      const rect = event.target.getBoundingClientRect();
      x = rect.left + rect.width / 2;
      y = rect.top - 10;
    }

    setTooltipData({
      odds,
      book,
      point,
      impliedProb: impliedProb.toFixed(2),
      edge: edge.toFixed(2),
      x,
      y,
      meta: betMeta,
    });
  };

  // Desktop tooltip closes on any outside click. On mobile the info renders
  // as a bottom sheet with an explicit close X, so we intentionally don't
  // auto-dismiss on outside taps — tapping another odds cell replaces it.
  useEffect(() => {
    if (!isMobile && tooltipData) {
      const handleClickOutside = (e) => {
        if (!e.target.closest('.odds-tooltip')) {
          setTooltipData(null);
        }
      };
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [tooltipData, isMobile]);

  // Fetch player data when modal opens
  useEffect(() => {
    if (show && playerId && gameId) {
      fetchPlayerData();
      // Reset chart state when modal opens
      setShowChart(false);
      setChartMarket(null);
      setChartPoint(null);
      setChartIsOver(true);
      setOddsHistoryData(null);
      setSelectedTier(null);
    }
  }, [show, playerId, gameId, market]);

  const fetchPlayerData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/games/soccer/${gameId}/${playerId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch player data');
      }

      const data = await response.json();

      // Soccer headshot URL format: https://media.api-sports.io/football/players/{playerId}.png
      const soccerPlayerId = playerId?.replace('soccer_', '');
      const photoUrl = soccerPlayerId
        ? `https://media.api-sports.io/football/players/${soccerPlayerId}.png`
        : null;

      const transformedData = {
        player: {
          name: data.playerName,
          team: data.team,
          teamLogo: data.teamLogo || null,
          position: playerPosition,
          photo: photoUrl,
          jerseyNumber: null
        },
        markets: []
      };

      // Process Soccer markets
      // Anytime Goal Scorer (binary market)
      if (data.player_goal_scorer_anytime && Array.isArray(data.player_goal_scorer_anytime)) {
        const anytimeGoalLines = processMarketLines(data.player_goal_scorer_anytime, 'player_goal_scorer_anytime');
        if (anytimeGoalLines.length > 0) {
          transformedData.markets.push({
            marketKey: 'player_goal_scorer_anytime',
            marketName: 'Anytime Goal',
            category: 'goals',
            lines: anytimeGoalLines
          });
        }
      }

      // First/Last Goal Scorer (combined market)
      const firstLastGoalMarket = {
        marketKey: 'player_first_last_goals',
        marketName: 'Goal Scorers',
        category: 'goal_scorers',
        lines: []
      };

      // Add first goal lines
      if (data.player_first_goal_scorer && Array.isArray(data.player_first_goal_scorer)) {
        const firstGoalLines = processMarketLines(data.player_first_goal_scorer, 'player_first_goal_scorer');
        firstLastGoalMarket.lines.push(...firstGoalLines);
      }

      // Add last goal lines
      if (data.player_last_goal_scorer && Array.isArray(data.player_last_goal_scorer)) {
        const lastGoalLines = processMarketLines(data.player_last_goal_scorer, 'player_last_goal_scorer');
        firstLastGoalMarket.lines.push(...lastGoalLines);
      }

      // Only add the market if it has lines
      if (firstLastGoalMarket.lines.length > 0) {
        transformedData.markets.push(firstLastGoalMarket);
      }

      // Shots
      if (data.player_shots && Array.isArray(data.player_shots)) {
        transformedData.markets.push({
          marketKey: 'player_shots',
          marketName: 'Shots',
          category: 'shots',
          lines: processMarketLines(data.player_shots, 'player_shots')
        });
      }

      // Shots on Target
      if (data.player_shots_on_target && Array.isArray(data.player_shots_on_target)) {
        transformedData.markets.push({
          marketKey: 'player_shots_on_target',
          marketName: 'Shots on Target',
          category: 'shots_on_target',
          lines: processMarketLines(data.player_shots_on_target, 'player_shots_on_target')
        });
      }

      // Assists
      if (data.player_assists && Array.isArray(data.player_assists)) {
        transformedData.markets.push({
          marketKey: 'player_assists',
          marketName: 'Assists',
          category: 'assists',
          lines: processMarketLines(data.player_assists, 'player_assists')
        });
      }

      setPlayerData(transformedData);

      // Set initial market type based on what's available
      if (transformedData.markets.length > 0) {
        setSelectedMarketType(transformedData.markets[0].category);
      }
    } catch (err) {
      console.error('Error fetching soccer player data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const processMarketLines = (marketLines, marketKey) => {
    if (!marketLines || !Array.isArray(marketLines)) return [];

    // New flat structure - just transform to modal format
    return marketLines.map(line => {
      // Convert flat structure to books object
      const books = {};

      // Extract all book data from the line
      Object.keys(line).forEach(key => {
        if (key.startsWith(`${marketKey}_`) && !key.includes('bestBook') && !key.includes('nextBook')) {
          const suffix = key.replace(`${marketKey}_`, '');
          const parts = suffix.split('_');

          if (parts.length >= 2) {
            const bookName = parts.slice(0, -1).join('_');
            const field = parts[parts.length - 1];

            if (field === 'over' || field === 'under' || field === 'point' || field === 'isAlternate' || field === 'yes' || field === 'no' || field === 'isAvailable') {
              if (!books[bookName]) {
                books[bookName] = {};
              }
              books[bookName][field] = line[key];
            }
            // Also capture isAvailable with _over or _under suffix
            if (field === 'over' || field === 'under') {
              const availKey = `${marketKey}_${bookName}_${field}_isAvailable`;
              if (line[availKey] !== undefined) {
                books[bookName][`${field}_isAvailable`] = line[availKey];
              }
            }
          } else if (parts.length === 1) {
            // For binary markets, odds are stored directly
            const bookName = parts[0];
            if (typeof line[key] === 'number') {
              if (!books[bookName]) {
                books[bookName] = {};
              }
              // Store as 'yes' for binary markets
              books[bookName]['yes'] = line[key];
            }
          }
        }
      });

      // Determine label based on market type
      let label;
      if (line.point === 'yes' || line.point === 'no') {
        if (marketKey === 'player_first_goal_scorer') {
          label = '1st';
        } else if (marketKey === 'player_last_goal_scorer') {
          label = 'Last';
        } else if (marketKey === 'player_goal_scorer_anytime') {
          label = 'Any';
        } else {
          label = 'Yes';
        }
      } else {
        label = `${line.point}`;
      }

      return {
        point: line.point === 'yes' ? null : line.point,
        label: label,
        books: books,
        bestBook: line[`${marketKey}_bestBook`],
        bestBookPrice: line[`${marketKey}_bestBookPrice`],
        bestBookDiff: line[`${marketKey}_bestBookDiff`],
        isSpecial: line.point === 'yes' || line.point === 'no',
        originalMarketKey: marketKey
      };
    }).sort((a, b) => {
      // Sort special markets (yes/no) to end
      if (a.isSpecial && !b.isSpecial) return 1;
      if (!a.isSpecial && b.isSpecial) return -1;
      return (a.point || 0) - (b.point || 0);
    });
  };

  // Fetch odds history for chart
  const fetchOddsHistory = async (marketKey, pointValue, isOver = true) => {
    setLoadingChart(true);
    setChartMarket(marketKey);
    setChartPoint(pointValue);
    setChartIsOver(isOver);
    setSelectedTier(pointValue);
    setShowChart(true);

    try {
      const response = await fetch(`/api/games/soccer/${gameId}/${playerId}/${marketKey}`);

      if (!response.ok) {
        throw new Error('Failed to fetch odds history');
      }

      const data = await response.json();
      setOddsHistoryData(data);
    } catch (err) {
      console.error('Error fetching odds history:', err);
      setOddsHistoryData(null);
    } finally {
      setLoadingChart(false);
    }
  };

  const handleBackToTable = () => {
    setShowChart(false);
    setChartMarket(null);
    setChartPoint(null);
    setSelectedTier(null);
    setOddsHistoryData(null);
  };

  // Prepare chart data
  const prepareChartData = () => {
    if (!oddsHistoryData || !oddsHistoryData.oddsHistory || oddsHistoryData.oddsHistory.length === 0) {
      return null;
    }

    const filteredHistory = oddsHistoryData.oddsHistory.filter(entry => {
      // Filter by market type if the field exists
      // Only filter if marketType is present and doesn't match
      if (entry.marketType && chartMarket && entry.marketType !== chartMarket) return false;

      // Filter by point value if specified
      if (selectedTier !== null && entry.point !== undefined) {
        const tolerance = 0.01;
        return Math.abs(entry.point - selectedTier) < tolerance;
      }
      return true;
    });

    // If no entries match, try without market type filter (API might not include it)
    const historyToUse = filteredHistory.length > 0 ? filteredHistory : oddsHistoryData.oddsHistory.filter(entry => {
      if (selectedTier !== null && entry.point !== undefined) {
        const tolerance = 0.01;
        return Math.abs(entry.point - selectedTier) < tolerance;
      }
      return true;
    });

    if (historyToUse.length === 0) return null;

    const bookColors = {
      fanduel: '#1A3D6C',
      draftkings: '#5DD335',
      betmgm: '#FFCC00',
      fanatics: '#002F6C',
      caesars: '#B87333',
      espn_bet: '#ED1C24',
      bet365: '#006600',
      betrivers: '#1E90FF',
      pointsbet: '#ED1C24',
      hard_rock: '#FFD700'
    };

    // Get all unique bookmakers from the data
    // Handle both flat format (entry.book) and nested format (entry.books)
    const allBookmakers = new Set();
    historyToUse.forEach(entry => {
      if (entry.book) {
        // Flat format: entry has a single book field
        allBookmakers.add(entry.book);
      } else if (entry.books && typeof entry.books === 'object') {
        // Nested format: entry.books contains all bookmaker data
        Object.keys(entry.books).forEach(book => {
          if (entry.books[book]) {
            const bookData = entry.books[book];
            if (bookData.over !== undefined || bookData.yes !== undefined ||
                bookData.under !== undefined || typeof bookData === 'number') {
              allBookmakers.add(book);
            }
          }
        });
      }
    });

    // Include every book; Settings → Graphs only sets the initial hidden flag below.
    const bookmakers = Array.from(allBookmakers);
    if (bookmakers.length === 0) return null;

    // Group entries by timestamp (normalize to nearest 5 minutes)
    const groupedByTimestamp = {};
    historyToUse.forEach(entry => {
      if (!entry.timestamp) return;
      const date = new Date(entry.timestamp);
      const minutes = date.getMinutes();
      const roundedMinutes = Math.round(minutes / 5) * 5;
      date.setMinutes(roundedMinutes);
      date.setSeconds(0);
      date.setMilliseconds(0);
      const normalizedTimestamp = date.toISOString();

      if (!groupedByTimestamp[normalizedTimestamp]) {
        groupedByTimestamp[normalizedTimestamp] = [];
      }
      groupedByTimestamp[normalizedTimestamp].push(entry);
    });

    const timestamps = Object.keys(groupedByTimestamp).sort((a, b) => new Date(a) - new Date(b));
    if (timestamps.length === 0) return null;

    const labels = timestamps.map(ts => {
      const date = new Date(ts);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    });

    // Prepare datasets for each bookmaker
    const datasetsWithLatestValues = bookmakers.map(bookmaker => {
      const data = timestamps.map(timestamp => {
        const entries = groupedByTimestamp[timestamp];
        const values = [];

        entries.forEach(entry => {
          let value;

          // Handle flat format (entry.book + entry.overOdds/yesOdds)
          if (entry.book === bookmaker) {
            if (chartIsOver) {
              value = entry.overOdds ?? entry.yesOdds;
            } else {
              value = entry.underOdds;
            }
          }
          // Handle nested format (entry.books[bookmaker])
          else if (entry.books && entry.books[bookmaker]) {
            const bookData = entry.books[bookmaker];
            if (chartIsOver) {
              if (bookData.over !== undefined) {
                value = parseFloat(bookData.over);
              } else if (bookData.yes !== undefined) {
                value = parseFloat(bookData.yes);
              } else if (typeof bookData === 'number') {
                value = parseFloat(bookData);
              }
            } else {
              if (bookData.under !== undefined) {
                value = parseFloat(bookData.under);
              }
            }
          }

          if (value !== undefined && value !== null && !isNaN(value)) {
            values.push(value);
          }
        });

        // If multiple values exist, use the best (highest) one
        if (values.length > 0) {
          return Math.max(...values);
        }
        return null;
      });

      // Check if this bookmaker has any data
      const hasData = data.some(value => value !== null);
      if (!hasData) return null;

      // Get latest value for sorting
      let latestValue = null;
      for (let i = data.length - 1; i >= 0; i--) {
        if (data[i] !== null) {
          latestValue = data[i];
          break;
        }
      }

      // Calculate point radius - show dot only when value changes
      const pointRadius = data.map((value, index) => {
        if (value === null) return 0;
        if (index === 0) return 4;
        if (index === data.length - 1) return 4;

        let prevValue = null;
        for (let i = index - 1; i >= 0; i--) {
          if (data[i] !== null) {
            prevValue = data[i];
            break;
          }
        }

        return (prevValue !== null && value !== prevValue) ? 4 : 0;
      });

      const displayName = BOOKMAKER_ALIASES[bookmaker] || bookmaker;

      return {
        label: displayName.charAt(0).toUpperCase() + displayName.slice(1).replace(/_/g, ' '),
        data,
        borderColor: bookColors[bookmaker] || '#888888',
        backgroundColor: bookColors[bookmaker] || '#888888',
        borderWidth: 2,
        tension: 0.1,
        pointRadius,
        pointHoverRadius: 6,
        spanGaps: true,
        // Initial hidden state from Settings → Graphs.
        hidden: isBookEnabledForGraphs ? !isBookEnabledForGraphs(bookmaker) : false,
        latestValue
      };
    }).filter(Boolean);

    // Sort datasets by latest value (highest first)
    const datasets = datasetsWithLatestValues
      .sort((a, b) => {
        const aValue = a.latestValue || -Infinity;
        const bValue = b.latestValue || -Infinity;
        return bValue - aValue;
      })
      .map(dataset => {
        const { latestValue, ...chartDataset } = dataset;
        return chartDataset;
      });

    if (datasets.length === 0) return null;

    return { labels, datasets };
  };

  const chartData = useMemo(() => {
    if (!showChart) return null;
    return prepareChartData();
  }, [showChart, oddsHistoryData, chartMarket, chartPoint, chartIsOver, selectedTier]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          font: { size: 11 }
        }
      },
      title: {
        display: true,
        text: `${chartMarket?.replace(/_/g, ' ')} ${selectedTier ? `(${chartIsOver ? 'Over' : 'Under'} ${selectedTier})` : ''}`,
        font: { size: 14 }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            if (value === null) return null;
            return `${context.dataset.label}: ${value > 0 ? '+' : ''}${value}`;
          }
        }
      }
    },
    scales: {
      x: {
        title: { display: true, text: 'Time' }
      },
      y: {
        title: { display: true, text: 'Odds' },
        ticks: {
          callback: (value) => value > 0 ? `+${value}` : value
        }
      }
    }
  };

  // Helper function to get tier color styling
  const getTierStyle = (odds, lineBooks) => {
    if (!odds || !lineBooks) return {};

    const allOdds = Object.values(lineBooks)
      .map(b => b.over || b.yes)
      .filter(o => o !== null && o !== undefined);

    if (allOdds.length < 2) return {};

    const bestOdds = Math.max(...allOdds);
    const secondBestOdds = Math.max(...allOdds.filter(o => o !== bestOdds));

    let diff;
    if (bestOdds > 0 && secondBestOdds < 0) {
      const spreadPositive = bestOdds - 100;
      const spreadNegative = secondBestOdds - (-100);
      const totalSpread = spreadPositive - spreadNegative;
      diff = Math.abs(totalSpread / secondBestOdds);
    } else {
      diff = (bestOdds - secondBestOdds) / Math.abs(secondBestOdds);
    }

    const bestOddsCount = allOdds.filter(o => o === bestOdds).length;
    const isBest = odds === bestOdds && bestOddsCount === 1;

    let style = {
      cursor: 'pointer',
      fontWeight: isBest ? 'bold' : 'normal'
    };

    if (isBest && allOdds.length > 1) {
      if (diff >= 0.25) {
        Object.assign(style, {
          backgroundColor: 'green',
          color: 'yellow',
          borderRadius: '3px'
        });
      } else if (diff >= 0.15) {
        Object.assign(style, {
          backgroundColor: '#F59E0B',
          color: '#1E40AF',
          borderRadius: '3px'
        });
      } else if (diff >= 0.05) {
        Object.assign(style, {
          backgroundColor: '#3B82F6',
          color: 'white',
          borderRadius: '3px'
        });
      } else {
        Object.assign(style, {
          color: '#F59E0B'
        });
      }
    }

    return style;
  };

  // Desktop: Books in rows, point values in columns
  const renderDesktopTable = (market) => {
    if (!market || !market.lines || market.lines.length === 0) {
      return <p className="text-muted">No odds available for this market.</p>;
    }

    const overUnderLines = market.lines.filter(line => !line.isSpecial);
    const specialLines = market.lines.filter(line => line.isSpecial);

    return (
      <div className="table-responsive">
        <Table bordered hover size="sm">
          <thead className="table-light">
            <tr>
              <th style={{ width: '120px', fontSize: '0.95rem' }}></th>
              {overUnderLines.map((line, idx) => (
                <th
                  key={`header-${idx}`}
                  style={{
                    textAlign: 'center',
                    padding: '8px',
                    fontSize: '0.95rem',
                    color: '#007bff',
                    cursor: 'pointer'
                  }}
                  onClick={() => fetchOddsHistory(market.marketKey, line.point, true)}
                >
                  {line.label}
                </th>
              ))}
              {specialLines.map((line, idx) => (
                <th
                  key={`special-${idx}`}
                  style={{
                    textAlign: 'center',
                    padding: '8px',
                    fontSize: '0.95rem',
                    color: '#007bff',
                    cursor: 'pointer'
                  }}
                  onClick={() => fetchOddsHistory(line.originalMarketKey || market.marketKey, null, true)}
                >
                  {line.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bookmakers.map(book => {
              const dataKey = getBookmakerDataKey(book);
              return (
                <tr key={book}>
                  <td style={{ padding: '8px', textAlign: 'center', fontSize: '0.9rem' }}>
                    <img
                      src={getBookLogoUrl(book)}
                      alt={book}
                      style={{ height: '20px', maxWidth: '80px' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </td>
                  {overUnderLines.map((line, idx) => {
                    const overOdds = line.books[dataKey]?.over;
                    const cellStyle = getTierStyle(overOdds, line.books);
                    const betMeta = { gameId, playerId, playerName, marketKey: market.marketKey, book, odds: overOdds, awayTeam: game?.away, homeTeam: game?.home, playerTeam, playerPosition, line: line.point, side: 'over' };

                    return (
                      <td
                        key={`${book}-${idx}`}
                        style={{
                          textAlign: 'center',
                          padding: '8px',
                          fontSize: '0.9rem',
                          cursor: overOdds ? 'pointer' : 'default',
                          fontWeight: 'bold',
                          ...cellStyle,
                          opacity: line.books[dataKey]?.over_isAvailable === false ? 0.25 : undefined
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (overOdds) {
                            handleOddsClick(e, overOdds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                          }
                          if (!isMobile && overOdds && onOddsClick) {
                            onOddsClick(gameId, playerId, playerName, market.marketKey, book, overOdds, game?.away, game?.home, playerTeam, playerPosition, line.point, 'over');
                          }
                        }}
                        onMouseEnter={(e) => {
                          if (!isMobile && overOdds) {
                            handleOddsClick(e, overOdds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                          }
                        }}
                        onMouseLeave={() => {
                          if (!isMobile) {
                            setTooltipData(null);
                          }
                        }}
                      >
                        {overOdds ? (overOdds > 0 ? `+${overOdds}` : overOdds) : '-'}
                      </td>
                    );
                  })}
                  {specialLines.map((line, idx) => {
                    const odds = line.books[dataKey]?.yes || line.books[dataKey]?.over;
                    const specialCellStyle = getTierStyle(odds, line.books);
                    const isAvailable = line.books[dataKey]?.yes_isAvailable !== false && line.books[dataKey]?.over_isAvailable !== false;
                    const specialMarketKey = line.originalMarketKey || market.marketKey;
                    const betMeta = { gameId, playerId, playerName, marketKey: specialMarketKey, book, odds, awayTeam: game?.away, homeTeam: game?.home, playerTeam, playerPosition, line: null, side: 'yes', point: line.label };

                    return (
                      <td
                        key={`special-${book}-${idx}`}
                        style={{
                          textAlign: 'center',
                          padding: '8px',
                          fontSize: '0.9rem',
                          cursor: odds ? 'pointer' : 'default',
                          fontWeight: 'bold',
                          ...specialCellStyle,
                          opacity: !isAvailable ? 0.25 : undefined
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (odds) {
                            handleOddsClick(e, odds, book, line.label, line.bestBook, line.bestBookDiff, line.books, betMeta);
                          }
                          if (!isMobile && odds && onOddsClick) {
                            onOddsClick(gameId, playerId, playerName, specialMarketKey, book, odds, game?.away, game?.home, playerTeam, playerPosition, null, 'yes');
                          }
                        }}
                        onMouseEnter={(e) => {
                          if (!isMobile && odds) {
                            handleOddsClick(e, odds, book, line.label, line.bestBook, line.bestBookDiff, line.books, betMeta);
                          }
                        }}
                        onMouseLeave={() => {
                          if (!isMobile) {
                            setTooltipData(null);
                          }
                        }}
                      >
                        {odds ? (odds > 0 ? `+${odds}` : odds) : '-'}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  // Mobile: Same as desktop - Books in rows, point values in columns
  const renderMobileTable = (market) => {
    if (!market || !market.lines || market.lines.length === 0) {
      return <p className="text-muted">No odds available for this market.</p>;
    }

    const overUnderLines = market.lines.filter(line => !line.isSpecial);
    const specialLines = market.lines.filter(line => line.isSpecial);

    return (
      <div style={{ overflowX: 'auto' }}>
        <Table bordered size="sm" style={{ fontSize: '0.85rem', minWidth: 'max-content' }}>
          <thead className="table-light">
            <tr>
              <th style={{ width: '60px', minWidth: '60px', fontSize: '0.85rem' }}></th>
              {overUnderLines.map((line, idx) => (
                <th
                  key={`header-${idx}`}
                  style={{
                    textAlign: 'center',
                    padding: '4px',
                    fontSize: '0.85rem',
                    color: '#007bff',
                    cursor: 'pointer',
                    minWidth: '50px'
                  }}
                  onClick={() => fetchOddsHistory(market.marketKey, line.point, true)}
                >
                  {line.label}
                </th>
              ))}
              {specialLines.map((line, idx) => (
                <th
                  key={`special-${idx}`}
                  style={{
                    textAlign: 'center',
                    padding: '4px',
                    fontSize: '0.85rem',
                    color: '#007bff',
                    cursor: 'pointer',
                    minWidth: '50px'
                  }}
                  onClick={() => fetchOddsHistory(line.originalMarketKey || market.marketKey, null, true)}
                >
                  {line.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bookmakers.map(book => {
              const dataKey = getBookmakerDataKey(book);
              return (
                <tr key={book}>
                  <td style={{ padding: '4px', fontSize: '0.9rem', textAlign: 'center' }}>
                    <img
                      src={getBookLogoUrl(book)}
                      alt={book}
                      style={{ height: '16px', maxWidth: '60px' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </td>
                  {overUnderLines.map((line, idx) => {
                    const overOdds = line.books[dataKey]?.over;
                    const cellStyle = getTierStyle(overOdds, line.books);
                    const betMeta = { gameId, playerId, playerName, marketKey: market.marketKey, book, odds: overOdds, awayTeam: game?.away, homeTeam: game?.home, playerTeam, playerPosition, line: line.point, side: 'over' };

                    return (
                      <td
                        key={`${book}-${idx}`}
                        style={{
                          textAlign: 'center',
                          padding: '4px',
                          fontSize: '0.9rem',
                          cursor: overOdds ? 'pointer' : 'default',
                          fontWeight: 'bold',
                          ...cellStyle,
                          opacity: line.books[dataKey]?.over_isAvailable === false ? 0.25 : undefined
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (overOdds) {
                            handleOddsClick(e, overOdds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                          }
                          if (!isMobile && overOdds && onOddsClick) {
                            onOddsClick(gameId, playerId, playerName, market.marketKey, book, overOdds, game?.away, game?.home, playerTeam, playerPosition, line.point, 'over');
                          }
                        }}
                        onMouseEnter={(e) => {
                          if (!isMobile && overOdds) {
                            handleOddsClick(e, overOdds, book, line.point, line.bestBook, line.bestBookDiff, line.books, betMeta);
                          }
                        }}
                        onMouseLeave={() => {
                          if (!isMobile) {
                            setTooltipData(null);
                          }
                        }}
                      >
                        {overOdds ? (overOdds > 0 ? `+${overOdds}` : overOdds) : '-'}
                      </td>
                    );
                  })}
                  {specialLines.map((line, idx) => {
                    const odds = line.books[dataKey]?.yes || line.books[dataKey]?.over;
                    const specialCellStyle = getTierStyle(odds, line.books);
                    const isAvailable = line.books[dataKey]?.yes_isAvailable !== false && line.books[dataKey]?.over_isAvailable !== false;
                    const specialMarketKey = line.originalMarketKey || market.marketKey;
                    const betMeta = { gameId, playerId, playerName, marketKey: specialMarketKey, book, odds, awayTeam: game?.away, homeTeam: game?.home, playerTeam, playerPosition, line: null, side: 'yes', point: line.label };

                    return (
                      <td
                        key={`special-${book}-${idx}`}
                        style={{
                          textAlign: 'center',
                          padding: '4px',
                          fontSize: '0.9rem',
                          cursor: odds ? 'pointer' : 'default',
                          fontWeight: 'bold',
                          ...specialCellStyle,
                          opacity: !isAvailable ? 0.25 : undefined
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (odds) {
                            handleOddsClick(e, odds, book, line.label, line.bestBook, line.bestBookDiff, line.books, betMeta);
                          }
                          if (!isMobile && odds && onOddsClick) {
                            onOddsClick(gameId, playerId, playerName, specialMarketKey, book, odds, game?.away, game?.home, playerTeam, playerPosition, null, 'yes');
                          }
                        }}
                        onMouseEnter={(e) => {
                          if (!isMobile && odds) {
                            handleOddsClick(e, odds, book, line.label, line.bestBook, line.bestBookDiff, line.books, betMeta);
                          }
                        }}
                        onMouseLeave={() => {
                          if (!isMobile) {
                            setTooltipData(null);
                          }
                        }}
                      >
                        {odds ? (odds > 0 ? `+${odds}` : odds) : '-'}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  };

  const currentMarket = playerData?.markets.find(m => m.category === selectedMarketType);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      fullscreen="sm-down"
      centered
      scrollable
      className="soccer-player-modal"
      backdropClassName="player-modal-backdrop"
    >
      {isMobile && <ModalBrandingBar />}
      <Modal.Header closeButton style={{ borderBottom: '2px solid #dee2e6' }}>
        <Modal.Title className="w-100">
          {playerData?.player && (
            <div className="d-flex align-items-center gap-3">
              <img
                src={playerData.player.photo}
                alt={playerName}
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid #dee2e6'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h5 className="mb-0">{playerName}</h5>
                </div>
                <div className="text-muted small d-flex align-items-center gap-2">
                  <img
                    src={playerData.player.teamLogo || (game && (
                      playerTeam === game.away || playerTeam === game.awayTeamFull
                        ? game.awayLogo
                        : playerTeam === game.home || playerTeam === game.homeTeamFull
                          ? game.homeLogo
                          : null
                    )) || getTeamLogoUrl(playerTeam)}
                    alt={playerTeam}
                    style={{ width: '20px', height: '20px' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <span>{playerTeam}</span>
                  {playerPosition && (
                    <Badge bg="secondary" className="ms-1">{playerPosition}</Badge>
                  )}
                  <AddToScratchpadLink
                    playerData={{ gameId, playerId, playerName, playerTeam, awayTeam: game?.away, homeTeam: game?.home }}
                    onToggleScratchpad={onToggleScratchpad}
                    isInScratchpad={isInScratchpad}
                  />
                </div>
              </div>
            </div>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
            <Spinner animation="border" />
          </div>
        )}

        {error && (
          <div className="alert alert-danger">
            <h5>Error loading player data</h5>
            <p>{error}</p>
            <button className="btn btn-primary btn-sm" onClick={fetchPlayerData}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && playerData && !showChart && (
          <>
            {/* Market tabs */}
            <div className="d-flex gap-2 mb-3 flex-wrap">
              {['goals', 'shots', 'shots_on_target', 'assists', 'goal_scorers'].map(category => {
                const market = playerData.markets.find(m => m.category === category);
                const hasData = !!market;
                const marketName = category === 'goals' ? 'Goals'
                  : category === 'shots' ? 'Shots'
                  : category === 'shots_on_target' ? 'SOT'
                  : category === 'assists' ? 'Assists'
                  : category === 'goal_scorers' ? '1st/Last'
                  : category;

                return (
                  <button
                    key={category}
                    className={`btn btn-sm ${selectedMarketType === category ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => hasData && setSelectedMarketType(category)}
                    disabled={!hasData}
                    style={{
                      opacity: hasData ? 1 : 0.5,
                      cursor: hasData ? 'pointer' : 'not-allowed'
                    }}
                  >
                    {marketName}
                  </button>
                );
              })}
            </div>

            {/* Market table */}
            {currentMarket ? (
              isMobile ? renderMobileTable(currentMarket) : renderDesktopTable(currentMarket)
            ) : (
              <p className="text-muted">Select a market to view odds.</p>
            )}

            {/* Helper text for chart functionality */}
            <div style={{
              textAlign: 'center',
              fontSize: '0.85rem',
              color: '#6c757d',
              marginTop: '16px',
              padding: '8px'
            }}>
              📊 Click a point value to see odds movement
            </div>
          </>
        )}

        {!loading && !error && showChart && (
          <>
            {/* Back Button */}
            <div className="mb-3">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleBackToTable}
              >
                ← Back to Odds Table
              </Button>
            </div>

            {loadingChart && (
              <div className="d-flex justify-content-center align-items-center" style={{ height: isMobile ? '70vh' : '400px' }}>
                <Spinner animation="border" role="status" variant="primary">
                  <span className="visually-hidden">Loading chart...</span>
                </Spinner>
              </div>
            )}

            {!loadingChart && chartData && (
              <div style={{ height: isMobile ? '70vh' : '400px' }}>
                <Line options={chartOptions} data={chartData} />
              </div>
            )}

            {!loadingChart && !chartData && (
              <div className="alert alert-info">
                No odds history available for this market.
              </div>
            )}
          </>
        )}

        {/* Desktop tooltip */}
        {tooltipData && !isMobile && (
          <div
            className="odds-tooltip"
            style={{
              position: 'fixed',
              top: tooltipData.y - 70,
              left: tooltipData.x,
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '0.85rem',
              zIndex: 9999,
              pointerEvents: 'none',
              whiteSpace: 'nowrap'
            }}
          >
            <div><strong>{tooltipData.book}</strong></div>
            <div>Odds: {tooltipData.odds > 0 ? `+${tooltipData.odds}` : tooltipData.odds}</div>
            <div>Implied: {tooltipData.impliedProb}%</div>
            {parseFloat(tooltipData.edge) > 0 && (
              <div style={{ color: '#4ade80' }}>Edge: +{tooltipData.edge}%</div>
            )}
          </div>
        )}

        {/* Mobile bottom-sheet with Add/Remove to Betslip */}
        {tooltipData && isMobile && (
          <div
            role="dialog"
            aria-label="Odds detail"
            style={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: '#1f2937',
              color: 'white',
              padding: '14px 16px calc(16px + env(safe-area-inset-bottom))',
              boxShadow: '0 -6px 20px rgba(0,0,0,0.5)',
              borderTop: '1px solid #374151',
              borderTopLeftRadius: '14px',
              borderTopRightRadius: '14px',
              zIndex: 1210,
              fontSize: '0.9rem',
              animation: 'toastFadeUp 0.2s ease-out',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
              <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>
                {tooltipData.book.charAt(0).toUpperCase() + tooltipData.book.slice(1)} ({tooltipData.point})
              </span>
              <button
                type="button"
                onClick={() => setTooltipData(null)}
                aria-label="Close"
                style={{ background: 'transparent', border: 'none', color: '#d1d5db', fontSize: '1.4rem', lineHeight: 1, padding: '4px 10px', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
              <div>
                <div style={{ color: '#9ca3af', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Odds</div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{tooltipData.odds > 0 ? `+${tooltipData.odds}` : tooltipData.odds}</div>
              </div>
              <div>
                <div style={{ color: '#9ca3af', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Implied</div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#60a5fa' }}>{tooltipData.impliedProb}%</div>
              </div>
              <div>
                <div style={{ color: '#9ca3af', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>Edge</div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem', color: parseFloat(tooltipData.edge) > 0 ? '#34d399' : '#f87171' }}>
                  {parseFloat(tooltipData.edge) > 0 ? '+' : ''}{tooltipData.edge}%
                </div>
              </div>
            </div>
            {(() => {
              const m = tooltipData.meta;
              if (!m) return null;
              const stripSportPrefix = (id) => String(id ?? '').replace(/^[a-z]+_/i, '');
              const normLine = (v) => {
                if (v == null || v === '') return v;
                const n = parseFloat(v);
                return Number.isFinite(n) ? n : v;
              };
              const targetPlayerId = stripSportPrefix(m.playerId);
              const targetLine = normLine(m.line);
              const existing = betslipItems.find(bet =>
                bet.gameId === m.gameId &&
                stripSportPrefix(bet.playerId) === targetPlayerId &&
                bet.market === m.marketKey &&
                normLine(bet.line) === targetLine &&
                bet.side === m.side
              );
              const btnBase = { width: '100%', marginTop: '12px', padding: '12px 14px', fontSize: '0.95rem', fontWeight: 700, border: 'none', borderRadius: '10px', cursor: 'pointer', color: '#fff' };
              if (existing) {
                return (
                  <button
                    type="button"
                    style={{ ...btnBase, backgroundColor: '#dc2626' }}
                    onClick={() => { if (onRemoveBet) onRemoveBet(existing.id); setTooltipData(null); }}
                  >
                    Remove from Betslip
                  </button>
                );
              }
              return (
                <button
                  type="button"
                  style={{ ...btnBase, backgroundColor: '#10b981' }}
                  onClick={() => {
                    if (onOddsClick) {
                      onOddsClick(
                        m.gameId, m.playerId, m.playerName, m.marketKey,
                        m.book, m.odds, m.awayTeam, m.homeTeam,
                        m.playerTeam, m.playerPosition, m.line, m.side
                      );
                    }
                    setTooltipData(null);
                  }}
                >
                  Add to Betslip
                </button>
              );
            })()}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default SoccerPlayerModal;
